export const FIDO_MOCK_CNPJ_DIGITS = "36422867808";

export const FIDO_MODAL_SHOWN_KEY = "valut-2fa-modal-shown";

export const STORAGE_PREFIX = "valut:2fa:";

export type FidoTouchPhase = "idle" | "waiting" | "success" | "error";

export interface FidoCredentialRecord {
  id: string;
  label: string;
  createdAt: string;
  transports: string[];
}

export type TwoFactorPolicyMethod =
  | "email"
  | "sms"
  | "fido"
  | "whatsapp"
  | "google_authenticator"
  | "transactional_password";

export type VerificationLoginMethod =
  | "email"
  | "sms"
  | "fido"
  | "whatsapp"
  | "google_authenticator";

export const MAX_SELECTED_LOGIN_METHODS = 4;

export interface TwoFactorPolicy {
  required: string[];
  allowed: TwoFactorPolicyMethod[];
}

export interface TwoFactorStorageRecord {
  credentials: FidoCredentialRecord[];
  preferredLogin: VerificationLoginMethod;
  selectedLoginMethods: VerificationLoginMethod[];
  googleAuthenticatorConfigured?: boolean;
  method?: "sms" | "fido" | "both";
  policy: TwoFactorPolicy;
}

export const defaultTwoFactorPolicy = (): TwoFactorPolicy => ({
  required: ["email"],
  allowed: ["email", "sms", "whatsapp", "fido", "google_authenticator", "transactional_password"],
});

export const pilotTwoFactorPolicy = (): TwoFactorPolicy => ({
  required: ["email"],
  allowed: ["email", "sms", "fido", "whatsapp", "google_authenticator"],
});

export const resolvePolicyForAccount = (
  accountKey: string,
  stored?: TwoFactorPolicy | null
): TwoFactorPolicy => {
  const base = isFidoPilotAccount(accountKey)
    ? pilotTwoFactorPolicy()
    : defaultTwoFactorPolicy();

  if (!stored?.allowed?.length) return base;

  const mergedAllowed = Array.from(
    new Set([...stored.allowed, ...base.allowed])
  ) as TwoFactorPolicyMethod[];

  return {
    required: stored.required?.length ? stored.required : base.required,
    allowed: mergedAllowed,
  };
};

export const VERIFICATION_METHOD_ORDER: VerificationLoginMethod[] = [
  "email",
  "sms",
  "whatsapp",
  "google_authenticator",
  "fido",
];

/** Prefer e-mail when otp_sent_via is missing — matches BaaS default single-channel dispatch. */
const OTP_DELIVERY_PRIORITY: VerificationLoginMethod[] = [
  "email",
  "sms",
  "whatsapp",
];

export const isVerificationLoginMethod = (
  value: string
): value is VerificationLoginMethod =>
  VERIFICATION_METHOD_ORDER.includes(value as VerificationLoginMethod);

/** Maps API two_factor_methods values to frontend login method ids. */
export const mapApiTwoFactorMethod = (
  method: string
): VerificationLoginMethod | null => {
  const normalized = method.toLowerCase().trim();
  if (normalized === "webauthn") return "fido";
  return isVerificationLoginMethod(normalized) ? normalized : null;
};

export const normalizeApiTwoFactorMethods = (
  methods: string[] | undefined | null
): VerificationLoginMethod[] => {
  if (!Array.isArray(methods) || methods.length === 0) {
    return ["email"];
  }

  const normalized = methods
    .map(mapApiTwoFactorMethod)
    .filter((method): method is VerificationLoginMethod => method !== null);

  if (normalized.length === 0) {
    return ["email"];
  }

  return Array.from(new Set(normalized)).sort(
    (a, b) =>
      VERIFICATION_METHOD_ORDER.indexOf(a) -
      VERIFICATION_METHOD_ORDER.indexOf(b)
  );
};

const OTP_LOGIN_METHODS = new Set<VerificationLoginMethod>([
  "email",
  "sms",
  "whatsapp",
]);

const sortVerificationMethods = (
  methods: VerificationLoginMethod[]
): VerificationLoginMethod[] =>
  Array.from(new Set(methods)).sort(
    (a, b) =>
      VERIFICATION_METHOD_ORDER.indexOf(a) -
      VERIFICATION_METHOD_ORDER.indexOf(b)
  );

/**
 * Whether a method selected in "Ativar no login" can actually be used on the
 * verification screen. OTP channels need server capability (or a soft default);
 * USB needs a registered key (local and/or has_web_authn); Google needs local confirm.
 */
const isConfiguredMethodAvailable = (
  method: VerificationLoginMethod,
  apiMethods: VerificationLoginMethod[],
  record: TwoFactorStorageRecord,
  options: { hasFidoKey?: boolean; hasWebAuthn?: boolean } = {}
): boolean => {
  const apiSet = new Set(apiMethods);
  const hasFido =
    (options.hasFidoKey ?? false) ||
    (options.hasWebAuthn ?? false) ||
    (record.credentials?.length ?? 0) > 0;

  if (OTP_LOGIN_METHODS.has(method)) {
    // Server lists sms/whatsapp on challenge login; allow selected OTP even when
    // the array is empty (legacy payloads) so device preferences still apply.
    if (apiMethods.length === 0) return true;
    return apiSet.has(method);
  }
  if (method === "fido") {
    // Do not require API to list fido/webauthn — "Ativar no login" is device-local
    // and login historically only returned sms/whatsapp.
    return hasFido;
  }
  if (method === "google_authenticator") {
    return record.googleAuthenticatorConfigured === true;
  }
  return false;
};

/**
 * Login method list = methods checked as principal/backups that are actually
 * available on this device. Falls back to API methods when nothing is selected.
 * Preferred / otp_sent_via is ordered first so the UI challenges a single principal.
 */
export const resolveVerificationPageMethods = (
  apiMethods: VerificationLoginMethod[],
  record: TwoFactorStorageRecord,
  options: {
    hasFidoKey?: boolean;
    hasWebAuthn?: boolean;
    isPilot?: boolean;
  } = {}
): VerificationLoginMethod[] => {
  const selected = record.selectedLoginMethods || [];

  let methods: VerificationLoginMethod[] = [];
  if (selected.length > 0) {
    const fromSelection = selected.filter((method) =>
      isConfiguredMethodAvailable(method, apiMethods, record, options)
    );
    if (fromSelection.length > 0) {
      methods = sortVerificationMethods(fromSelection);
    }
  }

  if (methods.length === 0) {
    const fromApi = apiMethods.filter(isVerificationLoginMethod);
    const fallback =
      fromApi.length > 0 ? fromApi : (["email"] as VerificationLoginMethod[]);
    methods = sortVerificationMethods(fallback);
  }

  const preferred =
    mapApiTwoFactorMethod(record.preferredLogin || "") ||
    (methods.includes("email") ? "email" : methods[0]);
  if (preferred && methods.includes(preferred)) {
    methods = [preferred, ...methods.filter((m) => m !== preferred)];
  }

  return methods;
};

export const buildGoogleAuthenticatorOtpauthUrl = (
  secret: string,
  accountLabel: string,
  issuer = "VALUT"
): string => {
  const trimmedSecret = (secret || "").trim();
  if (!trimmedSecret) return "";
  const label = encodeURIComponent(`${issuer}:${accountLabel || "conta"}`);
  const issuerParam = encodeURIComponent(issuer);
  return `otpauth://totp/${label}?secret=${encodeURIComponent(trimmedSecret)}&issuer=${issuerParam}&digits=6&period=30`;
};

/** @deprecated Prefer SecretTwoAuth from GET /v1/ib/accounts/me/authenticator */
export const getMockGoogleAuthenticatorSecret = (accountKey: string): string => {
  const digits = normalizeLoginDocument(accountKey) || accountKey || "valut";
  return `VALUT${digits.slice(-8).padStart(8, "0")}`;
};

export const resolveDefaultVerificationMethod = (
  methods: VerificationLoginMethod[],
  otpSentVia?: string | null
): VerificationLoginMethod => {
  if (methods.length === 0) return "email";

  const normalizedSentVia = mapApiTwoFactorMethod(otpSentVia || "");
  if (normalizedSentVia && methods.includes(normalizedSentVia)) {
    return normalizedSentVia;
  }

  for (const method of OTP_DELIVERY_PRIORITY) {
    if (methods.includes(method)) return method;
  }

  return methods[0];
};

export const getDefaultSelectedLoginMethods = (
  accountKey: string,
  policy?: TwoFactorPolicy | null
): VerificationLoginMethod[] => {
  const resolvedPolicy = policy || resolvePolicyForAccount(accountKey);
  const fromRequired = resolvedPolicy.required.filter(
    isVerificationLoginMethod
  );
  if (fromRequired.length > 0) {
    return fromRequired.slice(0, MAX_SELECTED_LOGIN_METHODS);
  }
  return ["email"];
};

const filterSelectableLoginMethods = (
  methods: VerificationLoginMethod[],
  policy: TwoFactorPolicy,
  options: {
    isPilot?: boolean;
    hasFidoKey?: boolean;
    hasGoogleAuthenticator?: boolean;
  } = {}
): VerificationLoginMethod[] => {
  const {
    isPilot = false,
    hasFidoKey = false,
    hasGoogleAuthenticator = false,
  } = options;
  const allowed = new Set(policy.allowed);

  return methods.filter((method) => {
    if (!allowed.has(method)) return false;
    if (method === "fido") return isPilot || hasFidoKey;
    if (method === "google_authenticator") return hasGoogleAuthenticator;
    return true;
  });
};

export const getEnabledLoginMethods = (
  policy: TwoFactorPolicy,
  options: {
    isPilot?: boolean;
    hasFidoKey?: boolean;
    hasGoogleAuthenticator?: boolean;
    selectedLoginMethods?: VerificationLoginMethod[];
  } = {}
): VerificationLoginMethod[] => {
  const {
    isPilot = false,
    hasFidoKey = false,
    hasGoogleAuthenticator = false,
    selectedLoginMethods,
  } = options;

  const candidates =
    selectedLoginMethods?.length &&
    selectedLoginMethods.length <= MAX_SELECTED_LOGIN_METHODS
      ? selectedLoginMethods
      : VERIFICATION_METHOD_ORDER.filter((method) =>
          policy.allowed.includes(method)
        );

  const filtered = filterSelectableLoginMethods(candidates, policy, {
    isPilot,
    hasFidoKey,
    hasGoogleAuthenticator,
  });

  const requiredMethods = (policy.required || []).filter(
    (m): m is VerificationLoginMethod =>
      isVerificationLoginMethod(m) && policy.allowed.includes(m)
  );

  const merged = [...filtered];
  for (const req of requiredMethods) {
    if (!merged.includes(req)) {
      merged.push(req);
    }
  }

  return merged.sort(
    (a, b) =>
      VERIFICATION_METHOD_ORDER.indexOf(a) -
      VERIFICATION_METHOD_ORDER.indexOf(b)
  );
};

export const getDefaultLoginMethod = (
  policy: TwoFactorPolicy,
  options: {
    isPilot?: boolean;
    hasFidoKey?: boolean;
    hasGoogleAuthenticator?: boolean;
    preferredLogin?: VerificationLoginMethod;
    selectedLoginMethods?: VerificationLoginMethod[];
  } = {}
): VerificationLoginMethod => {
  const enabled = getEnabledLoginMethods(policy, options);
  if (enabled.length === 0) return "email";

  if (
    options.preferredLogin &&
    enabled.includes(options.preferredLogin) &&
    (options.preferredLogin !== "fido" || options.hasFidoKey) &&
    (options.preferredLogin !== "google_authenticator" ||
      options.hasGoogleAuthenticator)
  ) {
    return options.preferredLogin;
  }

  for (const method of enabled) {
    return method;
  }

  return enabled.includes("email") ? "email" : enabled[0];
};

export const defaultTwoFactorRecord = (
  accountKey = "anonymous"
): TwoFactorStorageRecord => {
  const policy = resolvePolicyForAccount(accountKey);
  const selectedLoginMethods = getDefaultSelectedLoginMethods(
    accountKey,
    policy
  );

  return {
    credentials: [],
    preferredLogin: selectedLoginMethods[0] || "email",
    selectedLoginMethods,
    method: "sms",
    policy,
  };
};

export const normalizeLoginDocument = (
  value?: string | null
): string => (value || "").replace(/\D/g, "");

/** Canonical base64url for WebAuthn credential ids (register vs authenticate). */
export const normalizeCredentialId = (id?: string | null): string => {
  const trimmed = (id || "").trim();
  if (!trimmed) return "";

  const base64 = trimmed.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);

  try {
    const binary = atob(padded);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    let binaryOut = "";
    for (let j = 0; j < bytes.length; j += 1) {
      binaryOut += String.fromCharCode(bytes[j]);
    }
    let encoded = btoa(binaryOut);
    return encoded.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  } catch {
    return trimmed;
  }
};

export const isFidoPilotAccount = (
  usernameOrDocument?: string | null
): boolean =>
  normalizeLoginDocument(usernameOrDocument) === FIDO_MOCK_CNPJ_DIGITS;

export const getTwoFactorStorageKey = (accountKey: string): string =>
  `${STORAGE_PREFIX}${accountKey || "anonymous"}`;

export const readTwoFactorRecord = (
  accountKey: string
): TwoFactorStorageRecord => {
  try {
    const raw = localStorage.getItem(getTwoFactorStorageKey(accountKey));

    if (!raw) {
      return defaultTwoFactorRecord(accountKey);
    }

    const parsed = JSON.parse(raw) as Partial<TwoFactorStorageRecord>;
    const credentials = Array.isArray(parsed.credentials)
      ? parsed.credentials.map((item) => ({
          id: normalizeCredentialId(item.id),
          label: item.label,
          createdAt: item.createdAt,
          transports: Array.isArray(item.transports) ? item.transports : ["usb"],
        }))
      : [];

    const resolvedPolicy = resolvePolicyForAccount(accountKey, parsed.policy);
    const selectedLoginMethods = Array.isArray(parsed.selectedLoginMethods)
      ? parsed.selectedLoginMethods
          .filter(isVerificationLoginMethod)
          .slice(0, MAX_SELECTED_LOGIN_METHODS)
      : getDefaultSelectedLoginMethods(accountKey, resolvedPolicy);

    const safeSelected =
      selectedLoginMethods.length > 0
        ? selectedLoginMethods
        : getDefaultSelectedLoginMethods(accountKey, resolvedPolicy);

    let preferredLogin: VerificationLoginMethod = safeSelected.includes("email")
      ? "email"
      : safeSelected[0] || "email";

    if (
      parsed.preferredLogin &&
      isVerificationLoginMethod(parsed.preferredLogin) &&
      safeSelected.includes(parsed.preferredLogin)
    ) {
      preferredLogin = parsed.preferredLogin;
    } else if (
      credentials.length > 0 &&
      safeSelected.includes("fido") &&
      parsed.preferredLogin === "fido"
    ) {
      preferredLogin = "fido";
    }

    const googleAuthenticatorConfigured =
      parsed.googleAuthenticatorConfigured === true;

    return {
      ...defaultTwoFactorRecord(accountKey),
      ...parsed,
      credentials,
      preferredLogin,
      selectedLoginMethods: safeSelected,
      googleAuthenticatorConfigured,
      policy: resolvedPolicy,
    };
  } catch {
    return defaultTwoFactorRecord(accountKey);
  }
};

export const hasRegisteredFidoKey = (accountKey: string): boolean =>
  readTwoFactorRecord(accountKey).credentials.length > 0;

export const resolveTwoFactorAccountKey = (
  source: {
    username?: string | null;
    userDocument?: string | null;
    userDocumentRaw?: string | null;
    userId?: string | null;
  } = {}
): string => {
  // Prefer the authenticated login document. Never key 2FA / USB credentials off the
  // selected wallet account document (company CNPJ / shared account) — that leaks keys
  // across persons who open the same account in the home selector.
  const fromUsername = normalizeLoginDocument(source.username);
  if (fromUsername) return fromUsername;

  const fromDocument = normalizeLoginDocument(
    source.userDocumentRaw || source.userDocument
  );
  if (fromDocument) return fromDocument;

  return source.userId || "anonymous";
};

export const buildMockFidoAccessToken = (username: string): string =>
  `mock-fido-${normalizeLoginDocument(username)}-${Date.now()}`;

export const isMockFidoToken = (token?: string | null): boolean =>
  typeof token === "string" && token.startsWith("mock-fido-");
